import axios from "axios";
import { SagaIterator } from "redux-saga";
import { call, cancelled, put, takeLatest } from "redux-saga/effects";

import { callGet } from "../../http_client";
import { createGenerateReportHandler } from "../reports/sagas";

import {
  chartFetchRequest,
  chartFetchSuccess,
  chartSearchResultsFetchError,
  chartSearchResultsFetchRequest,
  chartSearchResultsFetchSuccess,
  conversionsChartFetchError,
  conversionsSummaryTableFetchError,
  conversionsTableFetchError,
  reportGeneratorTaskCreateRequest,
  tableFetchRequest,
  tableFetchSuccess,
  tableSummaryFetchRequest,
  tableSummaryFetchSuccess,
} from "./actions";
import { ConversionsActionTypes } from "./types";

export function* handleConversionsTableFetch(action: ReturnType<typeof tableFetchRequest>): SagaIterator {
  const source = axios.CancelToken.source();
  try {
    const response = yield call(callGet, "/conversions/", action.payload, source.token);
    yield put(tableFetchSuccess(response.data));
  } catch (err) {
    yield put(conversionsTableFetchError(err.message));
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

export function* handleConversionsSummaryFetch(action: ReturnType<typeof tableSummaryFetchRequest>): SagaIterator {
  const source = axios.CancelToken.source();
  try {
    const response = yield call(callGet, "/conversions/summary/", action.payload, source.token);
    yield put(tableSummaryFetchSuccess(response.data));
  } catch (err) {
    yield put(conversionsSummaryTableFetchError(err.message));
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

export function* handleConversionsChartFetch(action: ReturnType<typeof chartFetchRequest>): SagaIterator {
  const source = axios.CancelToken.source();
  try {
    const response = yield call(callGet, "/conversions/chart/", action.payload, source.token);
    yield put(chartFetchSuccess({ items: response.data }));
  } catch (err) {
    yield put(conversionsChartFetchError(err.message));
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

export function* handleConversionsChartSearchResultsFetch(
  action: ReturnType<typeof chartSearchResultsFetchRequest>
): SagaIterator {
  const source = axios.CancelToken.source();
  try {
    const { from, to, resolution, market, gateId } = action.payload;
    const response = yield call(
      callGet,
      "/conversions/chart/search-results/",
      { from, to, resolution, market, gateId },
      source.token
    );
    yield put(chartSearchResultsFetchSuccess({ items: response.data }));
  } catch (err) {
    yield put(chartSearchResultsFetchError(err.message));
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

const handleConversionsReportFetch = createGenerateReportHandler<ReturnType<typeof reportGeneratorTaskCreateRequest>>(
  "/conversions/report/"
);

function* watchConversionsSagas(): SagaIterator {
  yield takeLatest(ConversionsActionTypes.CHART_FETCH_REQUEST, handleConversionsChartFetch);
  yield takeLatest(ConversionsActionTypes.CHART_SEARCH_RESULTS_FETCH_REQUEST, handleConversionsChartSearchResultsFetch);
  yield takeLatest(ConversionsActionTypes.TABLE_FETCH_REQUEST, handleConversionsTableFetch);
  yield takeLatest(ConversionsActionTypes.TABLE_SUMMARY_FETCH_REQUEST, handleConversionsSummaryFetch);
  yield takeLatest(ConversionsActionTypes.GENERATE_REPORT_REQUEST, handleConversionsReportFetch);
}

export default watchConversionsSagas;
